import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Search, MessageSquareOff, AlertTriangle, XCircle, LifeBuoy } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

/**
 * EmployerPendingApproval — single screen used by the OnboardingRouter
 * whenever an employer's approval_status is pending, rejected or
 * suspended.  We fetch /onboarding/state to surface the sanitized
 * public reason (if the admin toggled it on).
 */
export default function EmployerPendingApproval() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/onboarding/state`, { withCredentials: true });
        if (!cancelled) setState(res.data);
      } catch { /* auth handled elsewhere */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const status = state?.approval_status;
  const publicReason = state?.approval_public_reason;

  if (status === 'rejected') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="employer-rejected-screen">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-rose-100 p-6 sm:p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <XCircle className="h-7 w-7 text-rose-600" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Account not approved</h1>
          <p className="text-sm text-slate-500 mt-2">
            An admin has reviewed your account and it has not been approved for hiring on OnSiteHand.
          </p>
          {publicReason && (
            <div
              className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-left"
              data-testid="employer-rejected-reason"
            >
              <p className="text-xs font-semibold text-rose-700 mb-1">Reason from admin</p>
              <p className="text-sm text-rose-900">{publicReason}</p>
            </div>
          )}
          <a
            className="btn-primary mt-6 inline-flex items-center gap-2"
            href="mailto:support@onsitehand.com?subject=Rejected%20account%20appeal"
            data-testid="employer-rejected-support"
          >
            <LifeBuoy className="h-4 w-4" /> Contact support to appeal
          </a>
        </div>
      </div>
    );
  }

  if (status === 'suspended') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="employer-suspended-screen">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-amber-100 p-6 sm:p-8">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-amber-700" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-center">Account suspended</h1>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Your account has been temporarily suspended. You can still view your
            existing chats, offers and billing, but you can&apos;t start new hiring actions.
          </p>
          {publicReason && (
            <div
              className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3"
              data-testid="employer-suspended-reason"
            >
              <p className="text-xs font-semibold text-amber-800 mb-1">Reason from admin</p>
              <p className="text-sm text-amber-900">{publicReason}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <button
              className="btn-secondary"
              onClick={() => navigate('/messages')}
              data-testid="suspended-view-chats-btn"
            >
              View past chats
            </button>
            <a
              className="btn-secondary text-center"
              href="mailto:support@onsitehand.com?subject=Suspension%20appeal"
              data-testid="employer-suspended-support"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Default: pending
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="employer-pending-screen">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <Clock className="h-7 w-7 text-amber-600" />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Your account is awaiting approval</h1>
        <p className="text-sm text-slate-500 mt-2">
          Thanks for signing up. An admin is reviewing your details — usually within one business day.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 text-left">
          <div className="p-3 bg-slate-50 rounded-lg">
            <Search className="h-4 w-4 text-emerald-600 mb-1" />
            <p className="text-xs font-semibold text-slate-800">You CAN browse</p>
            <p className="text-xs text-slate-500">Public trade search stays open while we review.</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <MessageSquareOff className="h-4 w-4 text-rose-600 mb-1" />
            <p className="text-xs font-semibold text-slate-800">You CAN&apos;T contact yet</p>
            <p className="text-xs text-slate-500">Messages, offers, phone unlocks and job posts unlock after approval.</p>
          </div>
        </div>
        <button className="btn-secondary mt-6" onClick={() => navigate('/search')} data-testid="pending-browse-btn">
          Browse trades meanwhile
        </button>
      </div>
    </div>
  );
}
